/**
 * 로트 맞춤 창 — 판매 상세의 [로트 맞춤] 이 연다(백엔드 V장 "로트 매칭" A · A · A · A + ⑤, 2026-11-05).
 * 품목 행마다 그 스탁(제품 × 컨디션)의 로트(구매 줄)를 늘어놓고, 로트마다 몇 장을 이 판매에 묶을지 친다(한 행이 로트 여럿에 나눠 맞는다 ①).
 * 처음 값 — 맞춘 것이 없는 행은 오래된 로트부터 수량만큼 채워 둔다(제안 ③ — 영업은 확인만), 맞춘 행은 지금 맞춤 그대로.
 * 로트의 상한 = 서버가 준 가용 + 이 행이 이미 그 로트에서 묶은 수. "구매 중 N" 은 보이기만 한다(⑤ — 입고 전 로트는 고를 수 없다).
 * 저장은 하지 않는다 — 답으로 부르는 쪽이 [로트 저장](PUT /:id/lines)을 부른다. 창은 document.body 에 붙였다가 닫으면 뗀다(취소 창 꼴 · mg-* 틀).
 */
import type { SaleItemLineChoice, SaleItemLineMatchInput, SalesOrderDetail, SalesOrderItemView, StockLineOption } from '../api/client';
import { assert } from '../lib/assert';
import { escapeHtml, fmtNumber } from '../lib/cells';
import { shortageWordsOf } from '../pages/sales-order/words';

/** 창 안 로트 한 줄 — 서버 로트(options)와 이미 묶었지만 가용이 0 이 된 로트(matched 에만 있음)를 한 꼴로 */
type LotLine = { line_key: string; option: StockLineOption | null; matched: number; cap: number };

/** 창을 열고 답을 기다린다. [저장] 이면 행마다의 맞춤(컨디션 없는 행은 뺌), [닫기] · 바깥 · Esc 면 null */
export async function openSalesLineMatchDialog(detail: SalesOrderDetail, choices: readonly SaleItemLineChoice[]): Promise<SaleItemLineMatchInput[] | null> {
  const host = document.createElement('div');
  host.innerHTML = renderLineMatchDialog(detail, choices);
  document.body.appendChild(host);

  return new Promise((resolve) => {
    function close(result: SaleItemLineMatchInput[] | null): void {
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    /** 칸 값으로 행마다의 맞춤 — 0 인 로트는 뺀다 */
    function inputsOf(): SaleItemLineMatchInput[] {
      return choices.map((choice) => ({
        quote_item_id: choice.quote_item_id,
        matches: [...host.querySelectorAll<HTMLInputElement>(`input[data-item="${cssValue(choice.quote_item_id)}"]`)]
          .map((input) => ({ line_key: lineKeyOf(input), quantity: quantityOf(input) }))
          .filter((match) => match.quantity > 0),
      }));
    }
    /** 행마다 "맞춤 n / N" — 수량을 넘거나 로트 상한을 넘으면 붉게, 하나라도 그러면 [저장] 잠금 */
    function refresh(): void {
      let bBlocked = false;
      for (const item of detail.items) {
        const inputs = [...host.querySelectorAll<HTMLInputElement>(`input[data-item="${cssValue(item.quote_item_id)}"]`)];
        const sum = inputs.reduce((total, input) => total + quantityOf(input), 0);
        const bOverCap = inputs.some((input) => quantityOf(input) > Number(input.max) || !Number.isInteger(quantityOf(input)) || quantityOf(input) < 0);
        const bOver = sum > item.quantity || bOverCap;
        bBlocked = bBlocked || bOver;
        const sumEl = host.querySelector<HTMLElement>(`[data-sum="${cssValue(item.quote_item_id)}"]`);
        if (sumEl !== null) {
          sumEl.textContent = `맞춤 ${fmtNumber(sum)} / ${fmtNumber(item.quantity)}${bOverCap ? ' — 로트 가용을 넘었습니다' : sum > item.quantity ? ' — 수량을 넘었습니다' : ''}`;
          sumEl.classList.toggle('text-danger', bOver);
        }
      }
      const button = host.querySelector<HTMLButtonElement>('[data-action="line-match-save"]');
      assert(button !== null, '맞춤 창은 [저장] 을 그렸다 — renderLineMatchDialog');
      button.disabled = bBlocked;
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close(null);
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'line-match-close') close(null);
      if (target.dataset.action === 'line-match-save') close(inputsOf());
    }

    host.addEventListener('click', handleClick);
    host.addEventListener('input', refresh);
    document.addEventListener('keydown', handleKey);
    refresh();
  });
}

/** 창 전체 — 품목 행마다 머리 + 로트 표 · [닫기] [저장] */
function renderLineMatchDialog(detail: SalesOrderDetail, choices: readonly SaleItemLineChoice[]): string {
  const sections = choices.map((choice) => renderItemSection(itemOf(detail, choice.quote_item_id), choice)).join('');
  return `
    <div class="mg-wrap" role="dialog" aria-label="로트 맞춤">
      <div class="mg-overlay" data-action="line-match-close"></div>
      <div class="mg-dialog lm-dialog">
        <h3 class="mg-title">로트 맞춤 — ${escapeHtml(detail.quote_no)}</h3>
        <p class="mg-note">품목마다 어느 로트(구매 건)에서 몇 장을 이 판매에 묶을지 고릅니다. 저장하면 그 로트의 실물이 판매 예정으로 잡히고, 원가 · 마진이 그 로트로 정해집니다.</p>
        ${sections}
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="line-match-close">닫기</button>
          <button type="button" class="btn-primary" data-action="line-match-save">저장</button>
        </div>
      </div>
    </div>`;
}

/** 품목 행 하나 — 머리(파트넘버 · 디스크립션 · 컨디션 · 수량 · 구매 필요 · 구매 중) + 로트 표 + 맞춤 합 */
function renderItemSection(item: SalesOrderItemView, choice: SaleItemLineChoice): string {
  const lots = lotLinesOf(choice);
  const initial = initialQuantitiesOf(lots, item.quantity, choice.matched.length > 0);
  const need = shortageWordsOf(item.shortage, item.purchase_needed);
  const rows = lots.length === 0
    ? `<tr><td colspan="6" class="ds-empty">고를 로트가 없습니다 — 가용 실물이 없습니다</td></tr>`
    : lots.map((lot, index) => renderLotRow(item, lot, initial[index] ?? 0)).join('');
  return `
        <div class="lm-item">
          <div class="lm-item-head">
            ${item.option_part === null || item.option_part === '' ? '' : `<span class="mono">${escapeHtml(item.option_part)}</span>`}
            <span class="sc-item-name">${escapeHtml(item.description ?? '')}</span>
            <span class="text-muted">${escapeHtml(item.condition ?? '컨디션 없음')} · 수량 ${fmtNumber(item.quantity)}</span>
            ${need === '' ? '' : `<span class="so-short">${escapeHtml(need)}</span>`}
            ${choice.incoming > 0 ? `<span class="lm-incoming">구매 중 ${fmtNumber(choice.incoming)}</span>` : ''}
          </div>
          <table class="pl-table lm-table">
            <thead><tr><th>구매일</th><th>구매처</th><th>구매번호</th><th class="pl-qty">단가</th><th class="pl-qty">가용</th><th class="pl-qty">묶을 수</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <p class="lm-sum" data-sum="${escapeHtml(item.quote_item_id)}"></p>
        </div>`;
}

function renderLotRow(item: SalesOrderItemView, lot: LotLine, initial: number): string {
  const option = lot.option;
  return `
            <tr>
              <td>${option === null || option.purchased_date === null ? '—' : escapeHtml(option.purchased_date)}</td>
              <td>${option === null ? escapeHtml(lotKeyWordOf(lot.line_key)) : escapeHtml(option.supplier_name ?? (option.source === 'none' ? lotKeyWordOf(lot.line_key) : '—'))}</td>
              <td class="mono">${option === null || option.purchase_no === null ? '—' : escapeHtml(option.purchase_no)}</td>
              <td class="pl-qty">${option === null || option.unit_price === null ? '—' : fmtNumber(option.unit_price)}</td>
              <td class="pl-qty">${fmtNumber(lot.cap)}</td>
              <td class="pl-qty"><input type="number" class="input lm-qty" min="0" max="${lot.cap}" step="1" value="${initial}"
                data-item="${escapeHtml(item.quote_item_id)}" data-line="${escapeHtml(lot.line_key)}" aria-label="묶을 수"></td>
            </tr>`;
}

/** 서버 로트 + 이미 묶었지만 가용이 남지 않은 로트. 상한 = 가용 + 이 행이 그 로트에서 묶은 수 */
function lotLinesOf(choice: SaleItemLineChoice): LotLine[] {
  const matchedOf = (lineKey: string): number => choice.matched.find((match) => match.line_key === lineKey)?.quantity ?? 0;
  const fromOptions: LotLine[] = choice.options.map((option) => ({ line_key: option.line_key, option, matched: matchedOf(option.line_key), cap: option.available + matchedOf(option.line_key) }));
  const optionKeys = new Set(choice.options.map((option) => option.line_key));
  const onlyMatched: LotLine[] = choice.matched.filter((match) => !optionKeys.has(match.line_key))
    .map((match) => ({ line_key: match.line_key, option: null, matched: match.quantity, cap: match.quantity }));
  return [...onlyMatched, ...fromOptions];
}

/** 처음 값 — 맞춘 행은 지금 맞춤, 아니면 오래된 로트(options 순)부터 수량만큼(제안 ③) */
function initialQuantitiesOf(lots: readonly LotLine[], quantity: number, bMatched: boolean): number[] {
  if (bMatched) return lots.map((lot) => lot.matched);
  let left = quantity;
  return lots.map((lot) => {
    const take = Math.min(lot.cap, left);
    left -= take;
    return take;
  });
}

/** 로트 열쇠의 사람 말 — 백엔드 이력 글(lineKeyWordOf)과 같은 말 */
function lotKeyWordOf(lineKey: string): string {
  if (lineKey.startsWith('ecount:')) {
    const rest = lineKey.slice('ecount:'.length);
    const seqAt = rest.lastIndexOf(':');
    return `전표 ${rest.slice(0, seqAt)} #${rest.slice(seqAt + 1)}`;
  }
  if (lineKey.startsWith('po:')) return `PO 줄 ${lineKey.slice('po:'.length)}`;
  const origin = lineKey.slice(lineKey.lastIndexOf(':') + 1);
  return `구매 기록 없음(${UNIT_ORIGIN_WORD[origin] ?? origin})`;
}

const UNIT_ORIGIN_WORD: Readonly<Record<string, string>> = { purchased: '구매', legacy: '옛 재고', found: '실사 발견', disassembled: '분해', assembled: '조립' };

function itemOf(detail: SalesOrderDetail, quoteItemId: string): SalesOrderItemView {
  const item = detail.items.find((row) => row.quote_item_id === quoteItemId);
  assert(item !== undefined, `맞춤 재료의 품목 행 ${quoteItemId} 가 상세에 없다 — 둘 다 그 판매의 부모 행을 준다`);
  return item;
}

function lineKeyOf(input: HTMLInputElement): string {
  const lineKey = input.dataset.line;
  assert(lineKey !== undefined, '묶을 수 칸은 data-line(로트 열쇠)을 가진다 — renderLotRow');
  return lineKey;
}

/** 칸 값 — 비면 0 */
function quantityOf(input: HTMLInputElement): number {
  return input.value.trim() === '' ? 0 : Number(input.value);
}

/** 속성 선택자 값 — quote_item_id 는 숫자 글이라 따옴표 안에서 그대로 */
function cssValue(text: string): string {
  return text.replace(/["\\]/g, '\\$&');
}
